/* eslint-disable no-console */
import React, { useState } from 'react';
import { FacetAccordion } from '@components';
import { Input, Label, Typography } from 'gd-design-library';
import styles from './RangeFilterFacet.module.scss';
import classnames from 'classnames/bind';
import { Skeleton } from '@mui/material';

const cx = classnames.bind(styles);

const CN = 'facet-range';
const countClassName = `${CN}__count`;
const nameClassName = `${CN}__name`;
const labelClassName = `${CN}__label`;
const labelTextClassName = `${CN}__label-text`;

export interface RangeFacetData {
  count: number;
  from: number;
  to: number;
}

type Range = [from: number, to: number];

interface Props {
  facet: string;
  label: string;
  ranges: RangeFacetData[];
  selectedTerms: string[];
  selectTerm: (term: string) => void;
  deleteTerm: (term: string) => void;
  isLoading?: boolean;
  /**
   * Optional function to format the range display value
   * @param from - The minimum value of the range
   * @param to - The maximum value of the range
   * @returns The formatted string to display
   */
  formatRangeValue?: (from: number, to: number) => string;
  /**
   * Optional function to generate a unique ID for each range
   * @param from - The minimum value of the range
   * @param to - The maximum value of the range
   * @returns A unique identifier string
   */
  getRangeId?: (from: number, to: number) => string;
}

const defaultFormatRangeValue = (from: number, to: number): string =>
  `${Math.ceil(from)} - ${Math.ceil(to)}`;

const defaultGetRangeId = (from: number, to: number): string => `${from}${to}`;

export const RangeFilterFacet: React.FC<Props> = ({
  label,
  ranges,
  selectedTerms,
  selectTerm,
  deleteTerm,
  isLoading,
  formatRangeValue = defaultFormatRangeValue,
  getRangeId = defaultGetRangeId
}) => {
  // Convert selectedTerms (JSON strings representing [from, to] arrays) to ranges for internal state
  const selectedRanges = selectedTerms
    .map(term => {
      try {
        const parsed = JSON.parse(term);
        if (Array.isArray(parsed) && parsed.length === 2 &&
            typeof parsed[0] === 'number' && typeof parsed[1] === 'number') {
          return parsed as Range;
        }
      } catch (error) {
        console.error(`Failed to parse range term: "${term}"`, error);
      }

      return null;
    })
    .filter((range): range is Range => range !== null);

  const [ checked, setChecked ] = useState<{id: string, range: Range, term: string}[]>(
    (): {id: string, range: Range, term: string}[] =>
      selectedRanges.map(([ from, to ]) => ({
        id: getRangeId(from, to),
        range: [ from, to ],
        term: JSON.stringify([ from, to ])
      }))
  );

  const getCheckHandler = (range: Range, internalId: string, term: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (event.target.checked) {
        const newItem = { id: internalId, range, term };
        setChecked(prev => [ ...prev, newItem ]);
        selectTerm(term);
      } else {
        setChecked(prev => prev.filter(({ id }) => id !== internalId));
        deleteTerm(term);
      }
    };

  return (
    <FacetAccordion label={ label }>
      { !isLoading ? (
        <Typography>
          {ranges?.map(({ from, to, count }) => {
            const internalId = getRangeId(from, to);
            const value = formatRangeValue(from, to);
            const term = JSON.stringify([ from, to ]);

            return (
              <Label key={ internalId } className={ cx(labelClassName) }>
                <Input
                  role='checkbox'
                  variant='checkbox'
                  className={ cx(nameClassName) }
                  checked={ checked.some(({ id }) => id === internalId) }
                  onChange={ getCheckHandler([ from, to ], internalId, term) }
                  disabled={ !count }
                />
                <div className={ cx(labelTextClassName) }>
                  {value} <span className={ cx(countClassName) }>({count})</span>
                </div>
              </Label>
            );
          })}
        </Typography>
      ) : (
        <>
          <Skeleton variant='text' height={ 30 }/>
          <Skeleton variant='text' height={ 30 }/>
          <Skeleton variant='text' height={ 30 }/>
          <Skeleton variant='text' height={ 30 }/>
        </>
      )}
    </FacetAccordion>
  );
};
